import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Cột kéo giãn được cho bảng danh sách. Độ rộng người dùng chỉnh được nhớ theo
 * trình duyệt (localStorage) — chỉ là tiện ích cá nhân, nên storage bị chặn
 * (private window, site data bị xoá) thì bảng vẫn chạy với độ rộng mặc định.
 *
 * Trả về:
 *   widthOf(key)            độ rộng hiện tại (px)
 *   startResize(key, e)     gắn vào onPointerDown của tay nắm
 *   nudge(key, delta)       bàn phím: ←/→ trên tay nắm
 *   resetWidth(key)         double-click tay nắm → về mặc định
 */
export const MIN_COLUMN_WIDTH = 64;
export const KEYBOARD_STEP = 16;

export const clampWidth = (width) => Math.max(MIN_COLUMN_WIDTH, Math.round(Number(width) || 0));

const readWidths = (storageKey) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeWidths = (storageKey, widths) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // Storage tắt: bỏ qua, độ rộng chỉ sống tới khi reload.
  }
};

export default function useResizableColumns(storageKey, defaults) {
  const [overrides, setOverrides] = useState(() => readWidths(storageKey));
  const latest = useRef(overrides);
  const dragging = useRef(false);
  const stopDrag = useRef(null);

  // Lưu khi độ rộng đổi — trừ lúc đang kéo (lưu một lần khi thả chuột).
  useEffect(() => {
    latest.current = overrides;
    if (!dragging.current) writeWidths(storageKey, overrides);
  }, [storageKey, overrides]);
  useEffect(() => () => stopDrag.current?.(), []);

  const widthOf = useCallback(
    (key) => overrides[key] ?? defaults[key] ?? MIN_COLUMN_WIDTH,
    [overrides, defaults]
  );

  const setWidth = useCallback(
    (key, width) => setOverrides((prev) => ({ ...prev, [key]: clampWidth(width) })),
    []
  );

  const startResize = useCallback((key, event) => {
    event.preventDefault();
    event.stopPropagation();
    stopDrag.current?.();

    const startX = event.clientX;
    const startWidth = widthOf(key);
    const onMove = (e) => setWidth(key, startWidth + (e.clientX - startX));
    const stop = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", stop);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      dragging.current = false;
      stopDrag.current = null;
      writeWidths(storageKey, latest.current);
    };

    dragging.current = true;
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", stop);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    stopDrag.current = stop;
  }, [widthOf, setWidth, storageKey]);

  const nudge = useCallback((key, delta) => setWidth(key, widthOf(key) + delta), [setWidth, widthOf]);

  const resetWidth = useCallback((key) => {
    setOverrides((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key)));
  }, []);

  return { widthOf, startResize, nudge, resetWidth };
}
